// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This uses BaseNotaryFactory instead of class inheritance

import { createBaseNotary, type IBaseNotary } from "../BaseNotaryFactory";
import type { DocumentAnchor } from "le-coffre-resources/dist/Notary";
import { is404Error } from "@Front/Utils/errorClassifiers";
import { buildEmptyPayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

interface DocumentAnchorsInstance {
	getByDocumentUid(documentUid: string): Promise<DocumentAnchor | null>;
	anchorDocument(documentUid: string): Promise<DocumentAnchor>;
	downloadCertificate(documentUid: string): Promise<Blob>;
}

// Module-level singleton instance
let instance: DocumentAnchorsInstance | null = null;
let baseNotaryInstance: IBaseNotary | null = null;

function createDocumentAnchors(): DocumentAnchorsInstance {
	if (!baseNotaryInstance) {
		baseNotaryInstance = createBaseNotary();
	}

	const baseNotary = baseNotaryInstance;
	const baseUrl = baseNotary.namespaceUrl.concat("/documents");

	return {
		getByDocumentUid: async (documentUid: string): Promise<DocumentAnchor | null> => {
			const url = new URL(baseUrl.concat(`/${documentUid}/anchor`));
			try {
				return await baseNotary.getRequest<DocumentAnchor>(url);
			} catch (err) {
				if (is404Error(err)) {
					return null;
				}
				baseNotary.onError(err);
				return Promise.reject(err);
			}
		},
		anchorDocument: async (documentUid: string): Promise<DocumentAnchor> => {
			const url = new URL(baseUrl.concat(`/${documentUid}/anchor`));
			try {
				return await baseNotary.postRequest<DocumentAnchor>(url, buildEmptyPayload());
			} catch (err) {
				baseNotary.onError(err);
				return Promise.reject(err);
			}
		},
		downloadCertificate: async (documentUid: string): Promise<Blob> => {
			const url = new URL(baseUrl.concat(`/${documentUid}/anchor/proof`));
			try {
				return await baseNotary.getRequest<Blob>(url, { fileName: `certificat-${documentUid}.pdf` });
			} catch (err) {
				baseNotary.onError(err);
				return Promise.reject(err);
			}
		},
	};
}

export function getDocumentAnchorsInstance(): DocumentAnchorsInstance {
	if (!instance) {
		instance = createDocumentAnchors();
	}
	return instance;
}
